import React, { useState, useEffect, useRef } from 'react';
import Modal from './Modal';
import Button from './Button';
import { toDateInputString, toTimeInputString } from '../../shared/utils.js';

interface ManualTimeEntryModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (timestamp: number) => void;
    title: string;
}

const ManualTimeEntryModal: React.FC<ManualTimeEntryModalProps> = ({ isOpen, onClose, onSave, title }) => {
    const formRef = useRef<HTMLFormElement>(null);
    const [date, setDate] = useState('');
    const [time, setTime] = useState('');

    useEffect(() => {
        if (isOpen) {
            const now = new Date();
            setDate(toDateInputString(now));
            setTime(toTimeInputString(now));
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                formRef.current?.requestSubmit();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onSave]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const timestamp = new Date(`${date}T${time}`).getTime();
        if (isNaN(timestamp)) {
            alert('Invalid date or time');
            return;
        }
        onSave(timestamp);
    };
    
    const inputClasses = "block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-secondary focus:border-brand-secondary";

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={title}>
            <form ref={formRef} onSubmit={handleSubmit} className="space-y-4 text-gray-900 dark:text-white">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="manual-date" className="block text-sm font-medium text-gray-600 dark:text-gray-300">Date</label>
                        <input
                            type="date"
                            id="manual-date"
                            value={date}
                            onChange={e => setDate(e.target.value)}
                            required
                            className={`mt-1 ${inputClasses}`}
                        />
                    </div>
                     <div>
                        <label htmlFor="manual-time" className="block text-sm font-medium text-gray-600 dark:text-gray-300">Time</label>
                        <input
                            type="time"
                            id="manual-time"
                            value={time}
                            onChange={e => setTime(e.target.value)}
                            required
                            className={`mt-1 ${inputClasses}`}
                        />
                    </div>
                </div>
                 <div className="flex justify-end gap-4 pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
                    <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button type="submit">OK</Button>
                </div>
            </form>
        </Modal>
    );
};

export default ManualTimeEntryModal;